import { useState } from 'react';
import type { FormEvent } from 'react';

export type CampoFormulario = {
  name: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'number' | 'textarea' | 'select';
  required?: boolean;
  placeholder?: string;
  options?: string[];
  half?: boolean;
  maxLength?: number;
};

interface OrcamentoFormShellProps {
  formId: string;
  submitAddr: string;
  campos: CampoFormulario[];
  submitLabel: string;
  nota?: string;
}

const inputClass =
  'w-full box-border border border-background-200 rounded-lg px-4 py-3 text-[14px] text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors';

const labelClass =
  'block text-[11px] font-medium text-foreground-500 mb-2 uppercase tracking-wider';

/** Lê uma string em um objeto desconhecido seguindo um caminho de chaves. */
function lerTexto(origem: unknown, caminho: string[]): string {
  let atual: unknown = origem;
  for (const chave of caminho) {
    if (typeof atual !== 'object' || atual === null) return '';
    atual = (atual as Record<string, unknown>)[chave];
  }
  return typeof atual === 'string' ? atual : '';
}

export default function OrcamentoFormShell({
  formId,
  submitAddr,
  campos,
  submitLabel,
  nota,
}: OrcamentoFormShellProps) {
  const [status, setStatus] = useState<'idle' | 'enviando' | 'sucesso' | 'erro'>('idle');
  const [mensagemErro, setMensagemErro] = useState('');
  const [caracteres, setCaracteres] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    // Verificação anti-spam antes de qualquer envio.
    const campoArmadilha = form.elements.namedItem('website_alt');
    const valorArmadilha =
      campoArmadilha instanceof HTMLInputElement ? campoArmadilha.value : '';
    if (valorArmadilha.trim() !== '') {
      setStatus('sucesso');
      form.reset();
      setCaracteres(0);
      return;
    }

    setStatus('enviando');
    setMensagemErro('');

    const dados = new FormData(form);
    const payload = new URLSearchParams();
    dados.forEach((valor, chave) => {
      if (chave === 'website_alt') return;
      const texto = String(valor).trim();
      if (texto) payload.append(chave, texto);
    });

    try {
      const resposta = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const textoResposta = await resposta.text();
      let corpo: unknown = null;
      try {
        corpo = JSON.parse(textoResposta);
      } catch {
        corpo = null;
      }

      const codigo = lerTexto(corpo, ['code']);
      const mensagemServidor =
        lerTexto(corpo, ['meta', 'message']) ||
        lerTexto(corpo, ['message']) ||
        lerTexto(corpo, ['meta', 'detail']);
      const respostaIndicaSpam = /spam/i.test(mensagemServidor) || /spam/i.test(textoResposta);
      const envioConfirmado = resposta.ok && codigo === 'OK' && !respostaIndicaSpam;

      if (!envioConfirmado) {
        setMensagemErro(
          mensagemServidor ||
            'Não foi possível enviar sua solicitação agora. Tente novamente ou fale com a gente no WhatsApp.'
        );
        setStatus('erro');
        return;
      }

      setStatus('sucesso');
      form.reset();
      setCaracteres(0);
    } catch {
      setMensagemErro(
        'Falha de conexão ao enviar. Verifique sua internet e tente novamente, ou fale com a gente no WhatsApp.'
      );
      setStatus('erro');
    }
  };

  if (status === 'sucesso') {
    return (
      <div className="bg-background-50 rounded-2xl p-10 text-center border border-background-200/60">
        <div className="w-14 h-14 flex items-center justify-center bg-primary-600 rounded-full mx-auto mb-4">
          <i className="ri-check-line text-white text-2xl"></i>
        </div>
        <h3 className="text-foreground-900 font-semibold text-xl mb-2">Solicitação registrada</h3>
        <p className="text-foreground-500 text-[14px] font-light leading-relaxed">
          Recebemos seus dados. Nossa equipe comercial entra em contato em até 24 horas úteis
          para entender a demanda e avaliar os itens compatíveis.
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-6 text-[13px] font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
        >
          Enviar outra solicitação
        </button>
      </div>
    );
  }

  return (
    <form id={formId} data-readdy-form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {campos.map((campo) => {
          const obrigatorio = campo.required === true;
          return (
            <div
              key={campo.name}
              className={`min-w-0 ${campo.half ? '' : 'sm:col-span-2'}`}
            >
              <label className={labelClass} htmlFor={`${formId}-${campo.name}`}>
                {campo.label}
                {obrigatorio ? ' *' : ''}
              </label>

              {campo.type === 'select' ? (
                <select
                  id={`${formId}-${campo.name}`}
                  name={campo.name}
                  required={obrigatorio}
                  defaultValue=""
                  className={`${inputClass} bg-white cursor-pointer`}
                >
                  <option value="">Selecione</option>
                  {(campo.options ?? []).map((opcao) => (
                    <option key={opcao} value={opcao}>
                      {opcao}
                    </option>
                  ))}
                </select>
              ) : campo.type === 'textarea' ? (
                <>
                  <textarea
                    id={`${formId}-${campo.name}`}
                    name={campo.name}
                    required={obrigatorio}
                    rows={5}
                    maxLength={campo.maxLength ?? 500}
                    placeholder={campo.placeholder}
                    onChange={(e) => setCaracteres(e.target.value.length)}
                    className={`${inputClass} resize-none`}
                  ></textarea>
                  <p className="text-[11px] text-foreground-300 mt-1">
                    {caracteres}/{campo.maxLength ?? 500} caracteres
                  </p>
                </>
              ) : (
                <input
                  id={`${formId}-${campo.name}`}
                  name={campo.name}
                  type={campo.type}
                  required={obrigatorio}
                  placeholder={campo.placeholder}
                  maxLength={campo.type === 'tel' ? 20 : undefined}
                  className={inputClass}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Campo de verificação anti-spam (não visível, não obrigatório) */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="hop-hp-field"
      />

      {status === 'erro' && mensagemErro ? (
        <div
          role="alert"
          className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-[13px] text-primary-800 leading-relaxed"
        >
          {mensagemErro}
        </div>
      ) : null}

      <button
        type="submit"
        disabled={status === 'enviando'}
        className="w-full bg-primary-600 text-white font-medium py-4 rounded-full hover:bg-primary-700 transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
      >
        {status === 'enviando' ? 'Enviando...' : submitLabel}
      </button>

      {nota ? (
        <p className="text-[11.5px] text-foreground-400 font-light leading-relaxed">{nota}</p>
      ) : null}
    </form>
  );
}